import {
  type AppConfig,
  type AutoFolderResult,
  type DeletionBatch,
  type OneFileUploadResult,
  type ThumbnailSize,
  type TtlKind,
  type UploadPreflightBatchRequest,
  type UploadPreflightRequest,
  type UploadPreflightResult,
  appConfigSchema,
  autoFolderResultSchema,
  deletionBatchSchema,
  oneFileUploadResultSchema,
  uploadPreflightBatchResultSchema,
  uploadPreflightResultSchema,
} from '@xfile/shared';
import { apiFetch } from '../lib/http';
import { SLOT_HEADER, getActiveSlot, withSlotParam } from '../lib/activeSlot';
import { UploadError, httpUploadError } from './upload-retry';
import { startWatchdog } from './upload-watchdog';
import { StorageApiError } from './storage-api';

/**
 * Client typé de l'UPLOAD et du contenu des fichiers (XFILE-03). Prolonge le client
 * de stockage (`storage-api.ts`), réutilise `apiFetch` (cookie de session +
 * `X-Active-Slot` + traçage) et normalise les erreurs en `StorageApiError`.
 *
 * Le téléchargement est browser-natif (`<a href>`), donc le slot actif NE peut pas
 * passer par en-tête : `fileContentUrl` le pose en query `?slot=N` via `withSlotParam`.
 */

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON.
  }
  return 'UNKNOWN';
}

/**
 * `GET /api/config` (public) — nom d'app + durées de conservation proposées (`uploadTtlKinds`)
 * et durée par défaut (`defaultTtlKind`, `null` = « aucun »). Amorce le sélecteur de TTL de
 * l'écran d'upload AVANT tout dépôt. Lève sur réponse non-OK.
 */
export async function fetchAppConfig(signal?: AbortSignal): Promise<AppConfig> {
  const res = await apiFetch('/api/config', { signal: signal ?? null });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}

/**
 * Valeur résolue + requête EN VOL de `/api/config`, partagées à l'échelle du module.
 * `null` = jamais chargée avec succès.
 */
let cachedConfig: AppConfig | null = null;
let inFlightConfig: Promise<AppConfig> | null = null;

/**
 * `GET /api/config` MÉMOÏSÉ pour la durée de la page (XFILE-46).
 *
 * POURQUOI. La configuration d'upload est quasi statique (durées proposées, durée par défaut,
 * plafond de taille) alors que les écrans qui la lisent montent et démontent sans arrêt :
 * naviguer accueil → dossier → sous-dossier redemandait la MÊME réponse à chaque étape, et
 * l'explorateur comme l'écran de dépôt la demandaient chacun de leur côté.
 *
 * DEUX niveaux : la valeur résolue (servie immédiatement, sans requête) et la requête en vol
 * (deux écrans qui montent ensemble partagent UN aller-retour au lieu d'en lancer deux).
 *
 * ⚠ Un ÉCHEC n'est pas mémoïsé — seuls les succès le sont. Sans cela, une panne réseau
 * passagère au premier montage condamnerait le sélecteur de durées pour toute la session,
 * et le bouton « Réessayer » (XFILE-46) rejouerait éternellement la même erreur en cache.
 *
 * Le `signal` de l'appelant n'annule PAS la requête partagée (elle sert d'autres écrans) : il
 * borne l'attente de CET appelant, à qui il revient de vérifier son propre signal avant de
 * poser un état. Le garde-temps de `apiFetch` (60 s) garantit qu'elle se règle toujours.
 */
export async function loadAppConfig(signal?: AbortSignal): Promise<AppConfig> {
  if (cachedConfig !== null) return cachedConfig;
  if (inFlightConfig === null) {
    inFlightConfig = fetchAppConfig()
      .then((cfg) => {
        cachedConfig = cfg;
        return cfg;
      })
      .finally(() => {
        // Libère la place : un échec doit pouvoir être RÉESSAYÉ (rien n'est mis en cache).
        inFlightConfig = null;
      });
  }
  const result = await inFlightConfig;
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  return result;
}

/** Oublie la configuration mémoïsée — réservé aux TESTS (isolation entre cas). */
export function resetAppConfigCache(): void {
  cachedConfig = null;
  inFlightConfig = null;
}

/**
 * PRÉFLIGHT d'un fichier (Lot F) — `POST /api/uploads/preflight` (JSON). Interroge le
 * serveur AVANT tout transfert : `duplicate`/`linked` = contenu DÉJÀ présent (rien à
 * envoyer), `needs_upload` = octets à transférer. Classe les échecs en {@link UploadError}
 * (récupérable vs fatal) pour piloter le réessai du moteur ; une coupure réseau (rejet brut
 * de `fetch`) est récupérable, un `abort` est fatal (`ABORT`).
 */
export async function preflightFile(
  req: UploadPreflightRequest,
  signal?: AbortSignal,
): Promise<UploadPreflightResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/uploads/preflight', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
      signal: signal ?? null,
    });
  } catch {
    if (signal?.aborted) throw new UploadError('fatal', 0, 'ABORT');
    throw new UploadError('recoverable', 0, 'NETWORK');
  }
  if (!res.ok) throw httpUploadError(res.status, await errorCodeOf(res));
  return uploadPreflightResultSchema.parse(await res.json());
}

/**
 * PRÉFLIGHT PAR LOT — `POST /api/uploads/preflight/batch` (XFILE-43). Un SEUL aller-retour
 * pour tout un lot, là où le préflight unitaire en coûtait un PAR FICHIER : sur le cas de
 * référence du produit (618 fichiers), 618 requêtes deviennent une seule — et un lot
 * intégralement DÉDUPLIQUÉ se règle sans transférer un octet ni ouvrir 618 connexions.
 *
 * ⚠ CONTRAT D'ALIGNEMENT POSITIONNEL : `results[i]` répond à `items[i]`. On ne ré-identifie
 * JAMAIS par `contentHash` — un même contenu peut légitimement apparaître deux fois dans un
 * lot sous deux noms (c'est le cas NORMAL de la dédup), et deux entrées porteraient alors le
 * même hash. Un lot de longueur différente est donc REFUSÉ (erreur fatale) plutôt que deviné :
 * mal réaligner attribuerait à un fichier le verdict d'un autre.
 *
 * L'appelant borne le lot à `BULK_FILES_MAX` (découpage au-delà). Le préflight UNITAIRE reste
 * en place : c'est le chemin de REPRISE, fichier par fichier, après rechargement d'onglet.
 */
export async function preflightBatch(
  req: UploadPreflightBatchRequest,
  signal?: AbortSignal,
): Promise<UploadPreflightResult[]> {
  let res: Response;
  try {
    res = await apiFetch('/api/uploads/preflight/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
      signal: signal ?? null,
    });
  } catch {
    if (signal?.aborted) throw new UploadError('fatal', 0, 'ABORT');
    throw new UploadError('recoverable', 0, 'NETWORK');
  }
  if (!res.ok) throw httpUploadError(res.status, await errorCodeOf(res));
  const parsed = uploadPreflightBatchResultSchema.parse(await res.json());
  if (parsed.results.length !== req.items.length) {
    throw new UploadError('fatal', res.status, 'BATCH_MISMATCH');
  }
  return parsed.results;
}

/** Paramètres du transfert d'UN fichier (Lot F). */
export interface UploadFileParams {
  file: File;
  folderId: string;
  ttlKind: TtlKind;
  contentHash: string;
  /** Progression du transfert (fraction 0..1) — via `xhr.upload.onprogress`. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * TRANSFÈRE UN fichier — `POST /api/uploads/file` (multipart) — en **`XMLHttpRequest`** (et
 * non `fetch`) pour disposer de `xhr.upload.onprogress` (progression d'upload, absente de
 * `fetch`). Les champs `folderId`, `ttlKind`, `contentHash` PRÉCÈDENT la partie fichier (le
 * serveur les lit AVANT le fichier). Porte le **slot actif** (`X-Active-Slot`, comme
 * `apiFetch`) ; le cookie de session part automatiquement en same-origin. Rejette avec une
 * {@link UploadError} classée : réseau/transfert bloqué = récupérable, 400 = fatal, abandon
 * explicite = fatal.
 *
 * Le transfert est surveillé par un WATCHDOG DE PROGRESSION (`upload-watchdog`) et NON par
 * `xhr.timeout` : un timeout absolu tuerait un gros fichier en bonne santé (cf. la
 * justification détaillée dans ce module).
 */
export function uploadOneFile(params: UploadFileParams): Promise<OneFileUploadResult> {
  return new Promise<OneFileUploadResult>((resolve, reject) => {
    const { file, folderId, ttlKind, contentHash, onProgress, signal } = params;
    if (signal?.aborted) {
      reject(new UploadError('fatal', 0, 'ABORT'));
      return;
    }

    // Champs AVANT la partie fichier (contrat serveur : lus avant le flux de contenu).
    const form = new FormData();
    form.append('folderId', folderId);
    form.append('ttlKind', ttlKind);
    form.append('contentHash', contentHash);
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/uploads/file');
    // Slot actif (multi-login), comme `apiFetch` — cookie de session automatique en same-origin.
    const slot = getActiveSlot();
    if (slot !== null) xhr.setRequestHeader(SLOT_HEADER, String(slot));

    // WATCHDOG DE PROGRESSION (jamais `xhr.timeout`, qui est ABSOLU et punirait les gros
    // fichiers) : on détecte l'ARRÊT du transfert, pas sa lenteur. Le déclenchement abandonne
    // la requête — `xhr.onabort` court alors, d'où le drapeau `stalled` qui lui fait rejeter
    // en TIMEOUT récupérable plutôt qu'en ABORT fatal.
    let stalled = false;
    const watchdog = startWatchdog(() => {
      stalled = true;
      xhr.abort();
    });

    const onAbort = (): void => xhr.abort();
    const detach = (): void => {
      watchdog.stop();
      signal?.removeEventListener('abort', onAbort);
    };
    if (signal) signal.addEventListener('abort', onAbort, { once: true });

    xhr.upload.onprogress = (e: ProgressEvent): void => {
      watchdog.ping();
      if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    // Corps entièrement remis au réseau : plus aucune progression n'est attendue, on passe à
    // la fenêtre LARGE (le serveur hache, contrôle l'intégrité, ré-encode éventuellement).
    xhr.upload.onloadend = (): void => watchdog.awaitResponse();
    xhr.onload = (): void => {
      detach();
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(oneFileUploadResultSchema.parse(JSON.parse(xhr.responseText)));
        } catch {
          reject(new UploadError('fatal', xhr.status, 'PARSE'));
        }
        return;
      }
      reject(httpUploadError(xhr.status, extractErrorCode(xhr.responseText)));
    };
    xhr.onerror = (): void => {
      detach();
      reject(new UploadError('recoverable', 0, 'NETWORK'));
    };
    xhr.onabort = (): void => {
      detach();
      // Distinguer les deux abandons : le nôtre (watchdog → transfert bloqué, RÉCUPÉRABLE, une
      // reprise a toutes ses chances) de celui de l'utilisateur (abandon explicite, FATAL).
      reject(
        stalled
          ? new UploadError('recoverable', 0, 'TIMEOUT')
          : new UploadError('fatal', 0, 'ABORT'),
      );
    };

    xhr.send(form);
  });
}

/** Lit `error.code` d'un corps JSON d'erreur (best-effort ; `undefined` si absent). */
function extractErrorCode(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { error?: { code?: string } };
    if (typeof parsed?.error?.code === 'string') return parsed.error.code;
  } catch {
    // Corps non-JSON.
  }
  return undefined;
}

/**
 * Crée un SOUS-DOSSIER DE LOT (Lot F) — `POST /api/uploads/auto-folder` — sous les dossiers
 * `auto` `AAAA/MM` du jour, et renvoie son id. Destination du dépôt quand aucun dossier n'est
 * imposé (rangement automatique). Lève sur réponse non-OK.
 */
export async function createAutoFolder(signal?: AbortSignal): Promise<AutoFolderResult> {
  const res = await apiFetch('/api/uploads/auto-folder', {
    method: 'POST',
    signal: signal ?? null,
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return autoFolderResultSchema.parse(await res.json());
}

/**
 * Supprime (soft-delete) un fichier. Lève sur 4xx (FORBIDDEN / NOT_FOUND). Renvoie l'ESTAMPILLE
 * du lot (XFILE-33), à rejouer dans `restoreDeletion` pour ANNULER exactement cette suppression.
 */
export async function deleteFile(id: string): Promise<DeletionBatch> {
  const res = await apiFetch(`/api/files/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return deletionBatchSchema.parse(await res.json());
}

/**
 * URL de téléchargement authentifiée d'un fichier, avec le slot actif en query (repli
 * browser-natif). Utilisable en `<a href>` (`download`) ou copiée comme lien d'accès.
 * Chemin relatif same-origin : l'appelant préfixe l'origine pour un lien absolu.
 */
export function fileContentUrl(id: string): string {
  return withSlotParam(`/api/files/${id}/content`);
}

/**
 * URL de PRÉVISUALISATION INLINE d'un fichier (`?inline=1` + slot actif). Le serveur y
 * répond en `Content-Disposition: inline` + en-têtes de durcissement (CSP `sandbox`,
 * `nosniff`) — nécessaire pour qu'un PDF s'AFFICHE dans un `<iframe>` (sinon le navigateur
 * force le téléchargement). `<img>`/`<video>` n'en ont pas besoin (le média se rend quelle
 * que soit la disposition) et continuent d'utiliser `fileContentUrl`.
 */
export function filePreviewUrl(id: string): string {
  return withSlotParam(`/api/files/${id}/content?inline=1`);
}

/**
 * URL de la VIGNETTE authentifiée d'un fichier (T13, explorateur). Rendue dans un `<img>`
 * (browser-natif) : le slot actif passe donc en query `?slot=N` (pas d'en-tête possible),
 * comme le téléchargement. Le serveur sert un WebP si une vignette existe, sinon une icône
 * de type (jamais 404 pour un fichier présent).
 */
export function fileThumbnailUrl(id: string, size: ThumbnailSize): string {
  return withSlotParam(`/api/files/${id}/thumbnail/${size}`);
}
